/**
 * src/Utilities/recaptcha.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Google reCAPTCHA v3 helper for Agent Portal player creation, recharge and
 * redeem.
 *
 * The script is loaded in explicit mode so the reCAPTCHA badge can be rendered
 * inline inside the form (instead of Google's floating corner badge). v3 has no
 * checkbox: grecaptcha.execute() mints a token, the
 * "verifyPlayerCreationCaptcha" Cloud Function scores it on the server and
 * returns a single-use ticket bound to that action, and the ticket travels with
 * the write it authorises.
 *
 * Environment:
 *   REACT_APP_RECAPTCHA_V3_SITE_KEY  Google reCAPTCHA v3 site key.
 *                                    When absent, reCAPTCHA is skipped.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Parse } from "parse";

export const RECAPTCHA_V3_SITE_KEY = (
  process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY || ""
).trim();

// Must match ACTIONS in the backend's cloud/security/recaptcha.js
export const CAPTCHA_ACTIONS = {
  CREATE_PLAYER: "agent_create_player",
  RECHARGE: "agent_recharge",
  REDEEM: "agent_redeem",
};

export const PLAYER_CREATE_ACTION = CAPTCHA_ACTIONS.CREATE_PLAYER;

// Window event that asks a verification panel to fetch a fresh ticket, e.g.
// after a failed write burned the current one. event.detail holds the action.
export const CAPTCHA_RESET_EVENT = "agent-captcha-reset";

const SCRIPT_ID = "recaptcha-explicit-script";
const EXECUTE_TIMEOUT_MS = 15000;

export const isRecaptchaConfigured = () => RECAPTCHA_V3_SITE_KEY.length > 0;

let scriptPromise = null;

/**
 * Injects the reCAPTCHA script once (explicit render mode) and resolves with
 * window.grecaptcha once it is ready.
 */
const loadRecaptcha = () => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const whenReady = () => {
      if (!window.grecaptcha) {
        reject(new Error("reCAPTCHA failed to initialise"));
        return;
      }
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };

    // Already present (e.g. loaded by the login page's v2 widget, which also
    // uses explicit mode).
    if (window.grecaptcha && window.grecaptcha.render) {
      whenReady();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");

    script.addEventListener("load", whenReady);
    script.addEventListener("error", () =>
      reject(new Error("reCAPTCHA script could not be loaded"))
    );

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    // Let the next attempt retry the injection instead of caching the failure.
    scriptPromise = null;
    throw error;
  });

  return scriptPromise;
};

/**
 * Renders the reCAPTCHA badge inline into `container` and returns the widget
 * id used by executeRecaptcha().
 */
export const renderRecaptchaWidget = async (container) => {
  const grecaptcha = await loadRecaptcha();
  return grecaptcha.render(container, {
    sitekey: RECAPTCHA_V3_SITE_KEY,
    size: "invisible",
    badge: "inline",
    theme: "dark",
  });
};

/**
 * Runs reCAPTCHA v3 on a rendered widget and resolves with the token.
 */
export const executeRecaptcha = async (widgetId, action) => {
  const grecaptcha = await loadRecaptcha();

  return Promise.race([
    grecaptcha.execute(widgetId, { action }),
    new Promise((_, reject) =>
      setTimeout(
        () =>
          reject(
            new Error(
              "reCAPTCHA did not respond. If you are on localhost, make sure localhost is added to the key's domains."
            )
          ),
        EXECUTE_TIMEOUT_MS
      )
    ),
  ]);
};

/**
 * Mints a v3 token for `action` on the given widget and has the server verify
 * it. Resolves with { ticket, score, enabled }. Throws a user-facing error on
 * failure or a score below the configured threshold.
 */
export const verifyCaptcha = async (widgetId, action) => {
  let token;
  try {
    token = await executeRecaptcha(widgetId, action);
  } catch (error) {
    throw new Error(
      error?.message ||
        "Could not run reCAPTCHA verification. Please check your connection and try again."
    );
  }

  if (!token) {
    throw new Error("reCAPTCHA verification failed. Please try again.");
  }

  const result = await Parse.Cloud.run("verifyPlayerCreationCaptcha", {
    token,
    action,
  });
  return {
    ticket: result?.ticket || null,
    score: result?.score,
    enabled: result?.enabled,
  };
};

// ── Ticket hand-off: verification panel -> dataProvider ─────────────────────
// One pending ticket per action, so an open recharge dialog cannot hand its
// ticket to a redeem.
const pendingTickets = {};

export const setPendingTicket = (action, ticket) => {
  if (ticket) pendingTickets[action] = ticket;
  else delete pendingTickets[action];
};

/**
 * Returns the ticket from the last successful verification of `action` and
 * clears it, so a ticket is never offered twice.
 */
export const takePendingTicket = (action) => {
  const ticket = pendingTickets[action];
  delete pendingTickets[action];
  return ticket || null;
};

export const requestCaptchaReset = (action) => {
  window.dispatchEvent(new CustomEvent(CAPTCHA_RESET_EVENT, { detail: action }));
};
